import React, { useEffect, useState } from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
  addDoc,
  collection,
  doc,
  getFirestore,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import app from "../../firebase.js";

const statusConfigurations = {
  en_attente_documents: {
    label: "Finalisation en attente",
    description:
      "Votre dossier est enregistré, mais sa transmission doit être finalisée.",
    progress: 20,
    color: "#d97706",
    background: "#fffbeb",
    borderColor: "#fde68a",
  },

  soumise: {
    label: "Demande soumise",
    description:
      "Votre dossier a bien été transmis à HDC Finance.",
    progress: 40,
    color: "#2563eb",
    background: "#eff6ff",
    borderColor: "#bfdbfe",
  },

  en_cours_etude: {
    label: "En cours d’étude",
    description:
      "Notre équipe examine actuellement votre dossier et vos justificatifs.",
    progress: 65,
    color: "#7c3aed",
    background: "#f5f3ff",
    borderColor: "#ddd6fe",
  },

  informations_complementaires: {
    label: "Informations complémentaires",
    description:
      "Des informations ou justificatifs complémentaires sont nécessaires.",
    progress: 75,
    color: "#ea580c",
    background: "#fff7ed",
    borderColor: "#fed7aa",
  },

  acceptee: {
    label: "Demande acceptée",
    description:
      "Votre demande de financement a reçu une réponse favorable.",
    progress: 100,
    color: "#15803d",
    background: "#f0fdf4",
    borderColor: "#bbf7d0",
  },

  refusee: {
    label: "Décision rendue",
    description:
      "Une décision a été prise concernant votre demande.",
    progress: 100,
    color: "#dc2626",
    background: "#fef2f2",
    borderColor: "#fecaca",
  },

  retiree: {
    label: "Demande retirée",
    description:
      "Vous avez retiré cette demande. Elle ne sera plus étudiée par HDC Finance.",
    progress: 0,
    color: "#64748b",
    background: "#f8fafc",
    borderColor: "#cbd5e1",
  },
};

const withdrawableStatuses = ["en_attente_documents", "soumise"];

const formatConversationDate = (timestamp) => {
  if (!timestamp?.toDate) {
    return "Envoi en cours…";
  }

  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(timestamp.toDate());
};

function FinanceRequests() {
  const navigate = useNavigate();

  const [currentUser, setCurrentUser] = useState(null);
  const [requests, setRequests] = useState([]);
  const [expandedRequestId, setExpandedRequestId] =
    useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [withdrawingRequestId, setWithdrawingRequestId] =
    useState(null);
  const [actionMessage, setActionMessage] = useState("");
  const [actionError, setActionError] = useState("");

  useEffect(() => {
    const auth = getAuth(app);
    const database = getFirestore(app);

    let unsubscribeRequests = () => {};

    const unsubscribeAuth = onAuthStateChanged(
      auth,
      (authenticatedUser) => {
        unsubscribeRequests();

        if (!authenticatedUser) {
          setCurrentUser(null);
          setRequests([]);
          setIsLoading(false);
          return;
        }

        setCurrentUser(authenticatedUser);
        setIsLoading(true);
        setLoadError("");

        const requestsQuery = query(
          collection(database, "financeRequests"),
          where("userId", "==", authenticatedUser.uid)
        );

        unsubscribeRequests = onSnapshot(
          requestsQuery,
          (snapshot) => {
            const userRequests = snapshot.docs
              .map((requestDocument) => ({
                id: requestDocument.id,
                ...requestDocument.data(),
              }))
              .sort((firstRequest, secondRequest) => {
                const firstDate =
                  firstRequest.createdAt?.toMillis?.() || 0;

                const secondDate =
                  secondRequest.createdAt?.toMillis?.() || 0;

                return secondDate - firstDate;
              });

            setRequests(userRequests);
            setIsLoading(false);
          },
          (error) => {
            console.error(
              "Erreur lors du chargement des demandes :",
              error
            );

            setLoadError(
              "Impossible de charger vos demandes pour le moment."
            );

            setIsLoading(false);
          }
        );
      }
    );

    return () => {
      unsubscribeRequests();
      unsubscribeAuth();
    };
  }, []);

  const formatMoney = (value) => {
    if (
      value === "" ||
      value === null ||
      value === undefined
    ) {
      return "Non renseigné";
    }

    const numericValue = Number(value);

    if (Number.isNaN(numericValue)) {
      return value;
    }

    return `${new Intl.NumberFormat("fr-FR").format(
      numericValue
    )} GNF`;
  };

  const formatDate = (timestamp) => {
    if (!timestamp?.toDate) {
      return "Date non disponible";
    }

    return new Intl.DateTimeFormat("fr-FR", {
      day: "2-digit",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(timestamp.toDate());
  };

  const formatDuration = (request) => {
    const duration = request.formData?.desiredDuration;
    const unit = request.formData?.durationUnit;

    if (!duration) {
      return "Non renseignée";
    }

    const unitLabels = {
      jours: Number(duration) > 1 ? "jours" : "jour",
      semaines:
        Number(duration) > 1 ? "semaines" : "semaine",
      mois: "mois",
      annees: Number(duration) > 1 ? "années" : "année",
    };

    return `${duration} ${unitLabels[unit] || unit || ""}`.trim();
  };

  const getStatusConfiguration = (status) => {
    return (
      statusConfigurations[status] ||
      statusConfigurations.soumise
    );
  };

  const toggleRequestDetails = (requestId) => {
    setExpandedRequestId((currentRequestId) =>
      currentRequestId === requestId ? null : requestId
    );
  };

  const handleWithdrawRequest = async (request) => {
    if (!currentUser || !withdrawableStatuses.includes(request.status)) {
      return;
    }

    const confirmed = window.confirm(
      `Voulez-vous vraiment retirer la demande ${
        request.reference || request.id
      } ?\n\nCette action est irréversible et le dossier ne sera plus étudié.`
    );

    if (!confirmed) {
      return;
    }

    setWithdrawingRequestId(request.id);
    setActionMessage("");
    setActionError("");

    try {
      const database = getFirestore(app);
      const requestReference = doc(
        database,
        "financeRequests",
        request.id
      );

      await updateDoc(requestReference, {
        status: "retiree",
        withdrawnAt: serverTimestamp(),
        statusUpdatedAt: serverTimestamp(),
        withdrawnBy: currentUser.uid,
      });

      setActionMessage(
        `La demande ${request.reference || request.id} a bien été retirée.`
      );
    } catch (error) {
      console.error("Erreur lors du retrait de la demande :", error);
      setActionError(
        "Impossible de retirer cette demande. Vérifiez les règles Firestore puis réessayez."
      );
    } finally {
      setWithdrawingRequestId(null);
    }
  };

  return (
    <div style={styles.page}>
      <header style={styles.header}>
        <div style={styles.headerContent}>
          <button
            type="button"
            style={styles.backButton}
            onClick={() => navigate("/consulting/finance")}
          >
            ← Retour à HDC Finance
          </button>

          <div style={styles.brand}>
            <div style={styles.brandIcon}>H</div>

            <div>
              <div style={styles.brandName}>
                HDC Finance
              </div>

              <div style={styles.brandDescription}>
                Espace personnel sécurisé
              </div>
            </div>
          </div>
        </div>
      </header>

      <main style={styles.main}>
        <section style={styles.introduction}>
          <div style={styles.badge}>
            📂 Suivi des dossiers
          </div>

          <h1 style={styles.title}>
            Mes demandes de financement
          </h1>

          <p style={styles.subtitle}>
            Consultez l’avancement de vos demandes et suivez les
            différentes étapes de leur traitement.
          </p>

          {currentUser?.email && (
            <div style={styles.accountInformation}>
              <span style={styles.accountIcon}>👤</span>

              <div>
                <span style={styles.accountLabel}>
                  Compte connecté
                </span>

                <strong style={styles.accountEmail}>
                  {currentUser.email}
                </strong>
              </div>
            </div>
          )}
        </section>

        {actionMessage && (
          <div style={styles.successAlert} role="status">
            ✓ {actionMessage}
          </div>
        )}

        {actionError && (
          <div style={styles.actionErrorAlert} role="alert">
            ⚠️ {actionError}
          </div>
        )}

        {isLoading && (
          <div style={styles.stateCard}>
            <div style={styles.loader} />

            <h2 style={styles.stateTitle}>
              Chargement de vos demandes
            </h2>

            <p style={styles.stateText}>
              Nous recherchons les dossiers associés à votre
              compte.
            </p>
          </div>
        )}

        {!isLoading && loadError && (
          <div style={styles.errorCard}>
            <div style={styles.stateIcon}>⚠️</div>

            <h2 style={styles.stateTitle}>
              Chargement impossible
            </h2>

            <p style={styles.stateText}>
              {loadError}
            </p>

            <button
              type="button"
              style={styles.secondaryButton}
              onClick={() => window.location.reload()}
            >
              Réessayer
            </button>
          </div>
        )}

        {!isLoading &&
          !loadError &&
          requests.length === 0 && (
            <div style={styles.stateCard}>
              <div style={styles.stateIcon}>📭</div>

              <h2 style={styles.stateTitle}>
                Aucune demande enregistrée
              </h2>

              <p style={styles.stateText}>
                Vous n’avez pas encore soumis de demande de
                financement avec ce compte.
              </p>

              <button
                type="button"
                style={styles.primaryButton}
                onClick={() =>
                  navigate("/consulting/finance")
                }
              >
                Faire une demande →
              </button>
            </div>
          )}

        {!isLoading &&
          !loadError &&
          requests.length > 0 && (
            <section style={styles.requestsSection}>
              <div style={styles.sectionHeading}>
                <div>
                  <h2 style={styles.sectionTitle}>
                    Vos dossiers
                  </h2>

                  <p style={styles.sectionDescription}>
                    {requests.length}{" "}
                    {requests.length > 1
                      ? "demandes trouvées"
                      : "demande trouvée"}
                  </p>
                </div>

                <button
                  type="button"
                  style={styles.newRequestButton}
                  onClick={() =>
                    navigate("/consulting/finance")
                  }
                >
                  + Nouvelle demande
                </button>
              </div>

              <div style={styles.requestsList}>
                {requests.map((request) => {
                  const status =
                    getStatusConfiguration(request.status);

                  const isExpanded =
                    expandedRequestId === request.id;

                  const canWithdraw =
                    withdrawableStatuses.includes(request.status);

                  const isWithdrawing =
                    withdrawingRequestId === request.id;

                  return (
                    <article
                      key={request.id}
                      style={styles.requestCard}
                    >
                      <div style={styles.requestHeader}>
                        <div style={styles.requestIdentity}>
                          <div style={styles.requestIcon}>
                            📄
                          </div>

                          <div>
                            <span
                              style={styles.referenceLabel}
                            >
                              Numéro du dossier
                            </span>

                            <h3
                              style={styles.requestReference}
                            >
                              {request.reference ||
                                request.id}
                            </h3>
                          </div>
                        </div>

                        <span
                          style={{
                            ...styles.statusBadge,
                            color: status.color,
                            background:
                              status.background,
                            borderColor:
                              status.borderColor,
                          }}
                        >
                          {status.label}
                        </span>
                      </div>

                      <div style={styles.requestSummary}>
                        <SummaryItem
                          label="Financement"
                          value={
                            request.financingLabel ||
                            "Non renseigné"
                          }
                        />

                        <SummaryItem
                          label="Montant demandé"
                          value={formatMoney(
                            request.formData
                              ?.requestedAmount
                          )}
                        />

                        <SummaryItem
                          label="Durée souhaitée"
                          value={formatDuration(request)}
                        />

                        <SummaryItem
                          label="Date de soumission"
                          value={formatDate(
                            request.createdAt
                          )}
                        />
                      </div>

                      <div style={styles.progressSection}>
                        <div style={styles.progressHeading}>
                          <div>
                            <strong
                              style={styles.progressTitle}
                            >
                              {status.label}
                            </strong>

                            <p
                              style={
                                styles.progressDescription
                              }
                            >
                              {status.description}
                            </p>
                          </div>

                          <strong
                            style={{
                              ...styles.progressPercentage,
                              color: status.color,
                            }}
                          >
                            {status.progress} %
                          </strong>
                        </div>

                        <div style={styles.progressTrack}>
                          <div
                            style={{
                              ...styles.progressBar,
                              width: `${status.progress}%`,
                              background: status.color,
                            }}
                          />
                        </div>
                      </div>

                      {request.adminMessage?.trim() && (
                        <div
                          style={{
                            ...styles.adminMessageCard,
                            borderColor: status.borderColor,
                            background: status.background,
                          }}
                        >
                          <div style={styles.adminMessageHeader}>
                            <span style={styles.adminMessageIcon}>
                              💬
                            </span>

                            <div>
                              <strong
                                style={{
                                  ...styles.adminMessageTitle,
                                  color: status.color,
                                }}
                              >
                                Message de HDC Finance
                              </strong>

                              {request.statusUpdatedAt && (
                                <span
                                  style={styles.adminMessageDate}
                                >
                                  Mis à jour le{" "}
                                  {formatDate(
                                    request.statusUpdatedAt
                                  )}
                                </span>
                              )}
                            </div>
                          </div>

                          <p style={styles.adminMessageText}>
                            {request.adminMessage}
                          </p>
                        </div>
                      )}

                      {request.adminMessage?.trim() && currentUser && (
                        <RequestConversation
                          request={request}
                          currentUser={currentUser}
                        />
                      )}

                      <div style={styles.cardActions}>
                        <span style={styles.documentCount}>
                          📎 {request.documentCount || 0}{" "}
                          justificatif
                          {request.documentCount > 1
                            ? "s"
                            : ""}
                        </span>

                        <div style={styles.requestButtons}>
                          {canWithdraw && (
                            <button
                              type="button"
                              style={{
                                ...styles.withdrawButton,
                                ...(isWithdrawing
                                  ? styles.disabledWithdrawButton
                                  : {}),
                              }}
                              disabled={isWithdrawing}
                              onClick={() =>
                                handleWithdrawRequest(request)
                              }
                            >
                              {isWithdrawing
                                ? "Retrait en cours…"
                                : "Retirer ma demande"}
                            </button>
                          )}

                          <button
                            type="button"
                            style={styles.detailsButton}
                            onClick={() =>
                              toggleRequestDetails(
                                request.id
                              )
                            }
                          >
                            {isExpanded
                              ? "Masquer les détails ↑"
                              : "Voir les détails ↓"}
                          </button>
                        </div>
                      </div>

                      {isExpanded && (
                        <div style={styles.detailsPanel}>
                          <DetailItem
                            label="Profil"
                            value={
                              request.profileLabel ||
                              "Non renseigné"
                            }
                          />

                          <DetailItem
                            label="Mode de transmission"
                            value={
                              request.deliveryMethod ===
                              "email"
                                ? "Justificatifs envoyés par e-mail"
                                : "En attente de transmission"
                            }
                          />

                          <DetailItem
                            label="Adresse e-mail"
                            value={
                              request.userEmail ||
                              "Non renseignée"
                            }
                          />

                          <DetailItem
                            label="Description du besoin"
                            value={
                              request.formData
                                ?.financingDescription ||
                              "Non renseignée"
                            }
                          />
                        </div>
                      )}
                    </article>
                  );
                })}
              </div>
            </section>
          )}
      </main>
    </div>
  );
}

function SummaryItem({ label, value }) {
  return (
    <div style={styles.summaryItem}>
      <span style={styles.summaryLabel}>
        {label}
      </span>

      <strong style={styles.summaryValue}>
        {value}
      </strong>
    </div>
  );
}

function DetailItem({ label, value }) {
  return (
    <div style={styles.detailItem}>
      <span style={styles.detailLabel}>
        {label}
      </span>

      <strong style={styles.detailValue}>
        {value}
      </strong>
    </div>
  );
}

function RequestConversation({ request, currentUser }) {
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [conversationError, setConversationError] = useState("");

  const isWithdrawn = request.status === "retiree";
  const unreadAdminMessages = messages.filter(
    (messageItem) =>
      messageItem.senderRole === "admin" &&
      messageItem.readByUser !== true
  );

  useEffect(() => {
    const database = getFirestore(app);
    const messagesQuery = query(
      collection(
        database,
        "financeRequests",
        request.id,
        "messages"
      ),
      orderBy("createdAt", "asc")
    );

    const unsubscribe = onSnapshot(
      messagesQuery,
      (snapshot) => {
        setMessages(
          snapshot.docs.map((messageDocument) => ({
            id: messageDocument.id,
            ...messageDocument.data(),
          }))
        );
        setConversationError("");
      },
      (error) => {
        console.error(
          "Erreur lors du chargement de la conversation :",
          error
        );
        setConversationError(
          "Impossible de charger la conversation pour le moment."
        );
      }
    );

    return () => unsubscribe();
  }, [request.id]);

  const handleSendReply = async () => {
    const trimmedReply = reply.trim();

    if (!trimmedReply || isSending || isWithdrawn) {
      return;
    }

    if (trimmedReply.length > 1000) {
      setConversationError(
        "Votre réponse ne doit pas dépasser 1 000 caractères."
      );
      return;
    }

    setIsSending(true);
    setConversationError("");

    try {
      const database = getFirestore(app);

      await addDoc(
        collection(
          database,
          "financeRequests",
          request.id,
          "messages"
        ),
        {
          text: trimmedReply,
          senderId: currentUser.uid,
          senderEmail: currentUser.email || "",
          senderRole: "user",
          readByUser: true,
          readByAdmin: false,
          createdAt: serverTimestamp(),
        }
      );

      setReply("");
    } catch (error) {
      console.error("Erreur lors de l’envoi de la réponse :", error);
      setConversationError(
        "Votre réponse n’a pas pu être envoyée. Vérifiez les règles Firestore puis réessayez."
      );
    } finally {
      setIsSending(false);
    }
  };

  const handleMarkAsRead = async () => {
    if (unreadAdminMessages.length === 0) {
      return;
    }

    setConversationError("");

    try {
      const database = getFirestore(app);
      const batch = writeBatch(database);

      unreadAdminMessages.forEach((messageItem) => {
        batch.update(
          doc(
            database,
            "financeRequests",
            request.id,
            "messages",
            messageItem.id
          ),
          {
            readByUser: true,
          }
        );
      });

      await batch.commit();
    } catch (error) {
      console.error(
        "Erreur lors de la confirmation de lecture :",
        error
      );
      setConversationError(
        "Impossible de marquer les messages comme lus."
      );
    }
  };

  return (
    <div style={styles.conversationCard}>
      <div style={styles.conversationHeader}>
        <div>
          <strong style={styles.conversationTitle}>
            Conversation avec HDC Finance
          </strong>
          <span style={styles.conversationSubtitle}>
            Vos échanges sont conservés dans l’historique du dossier.
          </span>
        </div>

        <div style={styles.conversationIndicators}>
          {unreadAdminMessages.length > 0 && (
            <button
              type="button"
              onClick={handleMarkAsRead}
              style={styles.unreadButton}
            >
              🔔 {unreadAdminMessages.length} nouveau
              {unreadAdminMessages.length > 1 ? "x" : ""} message
              {unreadAdminMessages.length > 1 ? "s" : ""} — Marquer comme lu
            </button>
          )}

          <span style={styles.messageCount}>
            {messages.length} message{messages.length > 1 ? "s" : ""}
          </span>
        </div>
      </div>

      {messages.length > 0 && (
        <div style={styles.conversationMessages}>
          {messages.map((messageItem) => {
            const isUserMessage = messageItem.senderRole === "user";

            return (
              <div
                key={messageItem.id}
                style={{
                  ...styles.conversationMessage,
                  ...(isUserMessage
                    ? styles.userConversationMessage
                    : styles.adminConversationMessage),
                }}
              >
                <div style={styles.messageAuthorRow}>
                  <strong style={styles.messageAuthor}>
                    {isUserMessage ? "Vous" : "HDC Finance"}
                  </strong>

                  <span style={styles.messageDate}>
                    {formatConversationDate(messageItem.createdAt)}
                  </span>
                </div>

                <p style={styles.conversationMessageText}>
                  {messageItem.text}
                </p>
              </div>
            );
          })}
        </div>
      )}

      {conversationError && (
        <div style={styles.conversationError} role="alert">
          ⚠️ {conversationError}
        </div>
      )}

      {isWithdrawn ? (
        <div style={styles.conversationLocked}>
          🔒 Cette conversation est fermée, car la demande a été retirée.
        </div>
      ) : (
        <div style={styles.replySection}>
          <label
            htmlFor={`reply-${request.id}`}
            style={styles.replyLabel}
          >
            Répondre à HDC Finance
          </label>

          <textarea
            id={`reply-${request.id}`}
            value={reply}
            onChange={(event) => setReply(event.target.value)}
            placeholder="Écrivez votre réponse ou précisez les informations demandées…"
            maxLength={1000}
            disabled={isSending}
            style={styles.replyInput}
          />

          <div style={styles.replyFooter}>
            <span style={styles.characterCount}>
              {reply.length}/1000 caractères
            </span>

            <button
              type="button"
              disabled={!reply.trim() || isSending}
              onClick={handleSendReply}
              style={{
                ...styles.sendReplyButton,
                ...(!reply.trim() || isSending
                  ? styles.disabledSendReplyButton
                  : {}),
              }}
            >
              {isSending ? "Envoi en cours…" : "Envoyer ma réponse"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    background:
      "linear-gradient(180deg, #070b18 0px, #0b1020 470px, #f8fafc 470px, #f8fafc 100%)",
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },

  header: {
    borderBottom:
      "1px solid rgba(255, 255, 255, 0.08)",
  },

  headerContent: {
    width: "calc(100% - 48px)",
    maxWidth: "1200px",
    minHeight: "82px",
    margin: "0 auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "20px",
    flexWrap: "wrap",
  },

  backButton: {
    padding: 0,
    border: "none",
    background: "transparent",
    color: "#cbd5e1",
    fontSize: "13px",
    fontWeight: "800",
    cursor: "pointer",
  },

  brand: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    color: "#ffffff",
  },

  brandIcon: {
    width: "40px",
    height: "40px",
    borderRadius: "13px",
    background:
      "linear-gradient(135deg, #7c3aed, #2563eb)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: "900",
  },

  brandName: {
    fontSize: "14px",
    fontWeight: "900",
  },

  brandDescription: {
    marginTop: "3px",
    color: "#94a3b8",
    fontSize: "10px",
  },

  main: {
    width: "calc(100% - 48px)",
    maxWidth: "1100px",
    margin: "0 auto",
    padding: "64px 0 90px",
  },

  introduction: {
    maxWidth: "760px",
    margin: "0 auto",
    textAlign: "center",
  },

  badge: {
    display: "inline-flex",
    padding: "8px 13px",
    border: "1px solid rgba(167, 139, 250, 0.28)",
    borderRadius: "999px",
    background: "rgba(124, 58, 237, 0.12)",
    color: "#c4b5fd",
    fontSize: "11px",
    fontWeight: "900",
  },

  title: {
    margin: "18px 0 0",
    color: "#ffffff",
    fontSize: "clamp(34px, 6vw, 52px)",
    lineHeight: 1.1,
    letterSpacing: "-0.04em",
    fontWeight: "900",
  },

  subtitle: {
    margin: "18px auto 0",
    maxWidth: "650px",
    color: "#cbd5e1",
    fontSize: "14px",
    lineHeight: 1.8,
  },

  accountInformation: {
    width: "fit-content",
    maxWidth: "100%",
    margin: "26px auto 0",
    padding: "11px 16px",
    border:
      "1px solid rgba(255, 255, 255, 0.12)",
    borderRadius: "14px",
    background: "rgba(255, 255, 255, 0.07)",
    display: "flex",
    alignItems: "center",
    gap: "11px",
    textAlign: "left",
  },

  accountIcon: {
    fontSize: "19px",
  },

  accountLabel: {
    display: "block",
    color: "#94a3b8",
    fontSize: "9px",
    fontWeight: "700",
    textTransform: "uppercase",
    letterSpacing: "0.08em",
  },

  accountEmail: {
    display: "block",
    marginTop: "3px",
    color: "#ffffff",
    fontSize: "12px",
    overflowWrap: "anywhere",
  },

  requestsSection: {
    marginTop: "62px",
  },

  successAlert: {
    maxWidth: "760px",
    margin: "28px auto 0",
    padding: "14px 17px",
    border: "1px solid #bbf7d0",
    borderRadius: "13px",
    background: "#f0fdf4",
    color: "#15803d",
    fontSize: "12px",
    fontWeight: "800",
    textAlign: "center",
  },

  actionErrorAlert: {
    maxWidth: "760px",
    margin: "28px auto 0",
    padding: "14px 17px",
    border: "1px solid #fecaca",
    borderRadius: "13px",
    background: "#fef2f2",
    color: "#b91c1c",
    fontSize: "12px",
    fontWeight: "800",
    textAlign: "center",
  },

  sectionHeading: {
    marginBottom: "22px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "16px",
    flexWrap: "wrap",
  },

  sectionTitle: {
    margin: 0,
    color: "#0f172a",
    fontSize: "25px",
    fontWeight: "900",
  },

  sectionDescription: {
    margin: "6px 0 0",
    color: "#64748b",
    fontSize: "12px",
  },

  newRequestButton: {
    padding: "12px 17px",
    border: "none",
    borderRadius: "12px",
    background:
      "linear-gradient(135deg, #7c3aed, #2563eb)",
    color: "#ffffff",
    fontSize: "12px",
    fontWeight: "900",
    cursor: "pointer",
  },

  requestsList: {
    display: "grid",
    gap: "20px",
  },

  requestCard: {
    padding: "25px",
    border: "1px solid #e2e8f0",
    borderRadius: "22px",
    background: "#ffffff",
    boxShadow:
      "0 16px 45px rgba(15, 23, 42, 0.07)",
  },

  requestHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "18px",
    flexWrap: "wrap",
  },

  requestIdentity: {
    display: "flex",
    alignItems: "center",
    gap: "13px",
  },

  requestIcon: {
    width: "48px",
    height: "48px",
    borderRadius: "14px",
    background: "#f5f3ff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "21px",
  },

  referenceLabel: {
    color: "#94a3b8",
    fontSize: "9px",
    fontWeight: "800",
    textTransform: "uppercase",
    letterSpacing: "0.08em",
  },

  requestReference: {
    margin: "5px 0 0",
    color: "#0f172a",
    fontSize: "17px",
    fontWeight: "900",
  },

  statusBadge: {
    padding: "7px 11px",
    border: "1px solid",
    borderRadius: "999px",
    fontSize: "10px",
    fontWeight: "900",
  },

  requestSummary: {
    marginTop: "24px",
    display: "grid",
    gridTemplateColumns:
      "repeat(auto-fit, minmax(170px, 1fr))",
    gap: "12px",
  },

  summaryItem: {
    padding: "14px",
    borderRadius: "13px",
    background: "#f8fafc",
  },

  summaryLabel: {
    display: "block",
    color: "#94a3b8",
    fontSize: "9px",
    fontWeight: "800",
    textTransform: "uppercase",
  },

  summaryValue: {
    display: "block",
    marginTop: "7px",
    color: "#334155",
    fontSize: "12px",
  },

  progressSection: {
    marginTop: "22px",
    padding: "17px",
    borderRadius: "15px",
    background: "#f8fafc",
  },

  progressHeading: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "18px",
  },

  progressTitle: {
    color: "#0f172a",
    fontSize: "12px",
  },

  progressDescription: {
    margin: "5px 0 0",
    color: "#64748b",
    fontSize: "10px",
    lineHeight: 1.5,
  },

  progressPercentage: {
    whiteSpace: "nowrap",
    fontSize: "13px",
  },

  progressTrack: {
    height: "7px",
    marginTop: "14px",
    borderRadius: "999px",
    background: "#e2e8f0",
    overflow: "hidden",
  },

  progressBar: {
    height: "100%",
    borderRadius: "999px",
    transition: "width 0.35s ease",
  },

  cardActions: {
    marginTop: "20px",
    paddingTop: "17px",
    borderTop: "1px solid #e2e8f0",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "14px",
    flexWrap: "wrap",
  },

  adminMessageCard: {
    marginTop: "16px",
    padding: "16px",
    border: "1px solid",
    borderRadius: "15px",
  },

  adminMessageHeader: {
    display: "flex",
    alignItems: "flex-start",
    gap: "10px",
  },

  adminMessageIcon: {
    width: "30px",
    height: "30px",
    display: "grid",
    placeItems: "center",
    borderRadius: "9px",
    background: "rgba(255,255,255,0.75)",
    fontSize: "14px",
    flexShrink: 0,
  },

  adminMessageTitle: {
    display: "block",
    fontSize: "12px",
    fontWeight: "900",
  },

  adminMessageDate: {
    display: "block",
    marginTop: "3px",
    color: "#64748b",
    fontSize: "9px",
  },

  adminMessageText: {
    margin: "12px 0 0",
    color: "#334155",
    fontSize: "12px",
    lineHeight: 1.7,
    whiteSpace: "pre-wrap",
    overflowWrap: "anywhere",
  },

  conversationCard: {
    marginTop: "14px",
    padding: "17px",
    border: "1px solid #ddd6fe",
    borderRadius: "15px",
    background: "#ffffff",
  },

  conversationHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "14px",
    flexWrap: "wrap",
  },

  conversationTitle: {
    display: "block",
    color: "#312e81",
    fontSize: "12px",
    fontWeight: "900",
  },

  conversationSubtitle: {
    display: "block",
    marginTop: "4px",
    color: "#64748b",
    fontSize: "10px",
  },

  messageCount: {
    padding: "5px 9px",
    borderRadius: "999px",
    background: "#f5f3ff",
    color: "#6d28d9",
    fontSize: "9px",
    fontWeight: "900",
  },

  conversationIndicators: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: "8px",
    flexWrap: "wrap",
  },

  unreadButton: {
    padding: "6px 10px",
    border: "1px solid #fbbf24",
    borderRadius: "999px",
    background: "#fffbeb",
    color: "#b45309",
    fontFamily: "inherit",
    fontSize: "9px",
    fontWeight: "900",
    cursor: "pointer",
  },

  conversationMessages: {
    marginTop: "16px",
    display: "grid",
    gap: "10px",
  },

  conversationMessage: {
    maxWidth: "82%",
    padding: "12px 14px",
    borderRadius: "13px",
  },

  userConversationMessage: {
    marginLeft: "auto",
    background: "#ede9fe",
    border: "1px solid #ddd6fe",
  },

  adminConversationMessage: {
    marginRight: "auto",
    background: "#eff6ff",
    border: "1px solid #bfdbfe",
  },

  messageAuthorRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    flexWrap: "wrap",
  },

  messageAuthor: {
    color: "#334155",
    fontSize: "10px",
    fontWeight: "900",
  },

  messageDate: {
    color: "#94a3b8",
    fontSize: "8px",
  },

  conversationMessageText: {
    margin: "7px 0 0",
    color: "#334155",
    fontSize: "11px",
    lineHeight: 1.6,
    whiteSpace: "pre-wrap",
    overflowWrap: "anywhere",
  },

  conversationError: {
    marginTop: "13px",
    padding: "10px 12px",
    border: "1px solid #fecaca",
    borderRadius: "10px",
    background: "#fef2f2",
    color: "#b91c1c",
    fontSize: "10px",
    fontWeight: "800",
  },

  replySection: {
    marginTop: "16px",
    paddingTop: "15px",
    borderTop: "1px solid #e2e8f0",
  },

  replyLabel: {
    display: "block",
    marginBottom: "8px",
    color: "#0f172a",
    fontSize: "11px",
    fontWeight: "900",
  },

  replyInput: {
    width: "100%",
    minHeight: "90px",
    padding: "12px",
    border: "1px solid #cbd5e1",
    borderRadius: "11px",
    background: "#ffffff",
    color: "#0f172a",
    fontFamily: "inherit",
    fontSize: "11px",
    lineHeight: 1.6,
    resize: "vertical",
    outline: "none",
    boxSizing: "border-box",
  },

  replyFooter: {
    marginTop: "9px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    flexWrap: "wrap",
  },

  characterCount: {
    color: "#94a3b8",
    fontSize: "9px",
  },

  sendReplyButton: {
    padding: "10px 14px",
    border: "none",
    borderRadius: "10px",
    background: "linear-gradient(135deg, #7c3aed, #2563eb)",
    color: "#ffffff",
    fontSize: "10px",
    fontWeight: "900",
    cursor: "pointer",
  },

  disabledSendReplyButton: {
    opacity: 0.55,
    cursor: "not-allowed",
  },

  conversationLocked: {
    marginTop: "15px",
    padding: "11px 13px",
    borderRadius: "10px",
    background: "#f1f5f9",
    color: "#64748b",
    fontSize: "10px",
    fontWeight: "800",
  },

  documentCount: {
    color: "#64748b",
    fontSize: "11px",
    fontWeight: "700",
  },

  detailsButton: {
    padding: 0,
    border: "none",
    background: "transparent",
    color: "#6d28d9",
    fontSize: "11px",
    fontWeight: "900",
    cursor: "pointer",
  },

  requestButtons: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: "14px",
    flexWrap: "wrap",
  },

  withdrawButton: {
    padding: "9px 13px",
    border: "1px solid #fecaca",
    borderRadius: "10px",
    background: "#fff1f2",
    color: "#be123c",
    fontSize: "10px",
    fontWeight: "900",
    cursor: "pointer",
  },

  disabledWithdrawButton: {
    opacity: 0.6,
    cursor: "not-allowed",
  },

  detailsPanel: {
    marginTop: "18px",
    padding: "18px",
    border: "1px solid #e2e8f0",
    borderRadius: "15px",
    background: "#fafafa",
    display: "grid",
    gridTemplateColumns:
      "repeat(auto-fit, minmax(190px, 1fr))",
    gap: "14px",
  },

  detailItem: {
    minWidth: 0,
  },

  detailLabel: {
    display: "block",
    color: "#94a3b8",
    fontSize: "9px",
    fontWeight: "800",
    textTransform: "uppercase",
  },

  detailValue: {
    display: "block",
    marginTop: "6px",
    color: "#334155",
    fontSize: "11px",
    lineHeight: 1.6,
    overflowWrap: "anywhere",
  },

  stateCard: {
    maxWidth: "560px",
    margin: "70px auto 0",
    padding: "42px 30px",
    border: "1px solid #e2e8f0",
    borderRadius: "22px",
    background: "#ffffff",
    boxShadow:
      "0 18px 45px rgba(15, 23, 42, 0.08)",
    textAlign: "center",
  },

  errorCard: {
    maxWidth: "560px",
    margin: "70px auto 0",
    padding: "42px 30px",
    border: "1px solid #fecaca",
    borderRadius: "22px",
    background: "#ffffff",
    textAlign: "center",
  },

  loader: {
    width: "38px",
    height: "38px",
    margin: "0 auto",
    border: "4px solid #e2e8f0",
    borderTopColor: "#7c3aed",
    borderRadius: "50%",
  },

  stateIcon: {
    fontSize: "40px",
  },

  stateTitle: {
    margin: "18px 0 0",
    color: "#0f172a",
    fontSize: "22px",
    fontWeight: "900",
  },

  stateText: {
    margin: "12px auto 24px",
    maxWidth: "420px",
    color: "#64748b",
    fontSize: "13px",
    lineHeight: 1.7,
  },

  primaryButton: {
    padding: "14px 20px",
    border: "none",
    borderRadius: "12px",
    background:
      "linear-gradient(135deg, #7c3aed, #2563eb)",
    color: "#ffffff",
    fontSize: "12px",
    fontWeight: "900",
    cursor: "pointer",
  },

  secondaryButton: {
    padding: "13px 19px",
    border: "1px solid #cbd5e1",
    borderRadius: "12px",
    background: "#ffffff",
    color: "#334155",
    fontSize: "12px",
    fontWeight: "900",
    cursor: "pointer",
  },
};

export default FinanceRequests;